import { DateTime } from 'luxon';

import {
    ABTestType,
    CampaignChannels,
    EmailSendInput,
    EmailSendInputState,
    ExitActionInput,
    SplitActions,
    SplitTypes,
} from 'domains/campaigns/types';
import {
    EngagementAPIOperator,
    EngagementName,
    EngagementUIOperator,
    SegmentDemographic,
} from 'domains/segments/types';
import {
    convertFilterToEvent,
    engagementUiToApiOperator,
    getInclusionOperatorFromUIToAPI,
} from 'domains/segments/utils';
import { FilterCategory } from 'models/enums';

import { CampaignSend, CampaignStateTypes } from './customCampaignReducer';

/**
 * Helper to check if an operator is already in API format
 */
const isApiOperator = (operator: string): boolean =>
    Object.values(EngagementAPIOperator).includes(operator as EngagementAPIOperator);

export const getEffectiveSplitType = (send: any, sends: CampaignSend[]): string | undefined => {
    if (send?.splitType) return send.splitType;
    if (send?.parentIndex != null) return getEffectiveSplitType(sends[send.parentIndex], sends);
    return undefined;
};

/**
 * Returns true when a send is the ROOT of a conditional split branch (the send that
 * opens a MEETS / DOES_NOT_MEET split), as opposed to a chained send that merely lives
 * inside a branch.
 *
 * - Top-level split: the parent is a plain send with no splitType.
 * - Nested split: the send carries its own `splitNodeId`, distinct from the branch it
 *   lives in. This is what distinguishes a nested split's branch root from a chained send,
 *   which inherits the branch's splitType but has no splitNodeId of its own. Without this,
 *   a MEETS split nested inside a MEETS branch is indistinguishable from an inherited chain.
 *
 * Mirrors `isSplitBranchRoot` in utils/buildCampaignGraph.ts (parentIndex-based here).
 */
export const isSplitBranchRootSend = (send: any, sends: CampaignSend[]): boolean => {
    if (!send?.splitType) return false;
    const parent = send.parentIndex != null ? (sends[send.parentIndex] as any) : null;
    if (!parent?.splitType) return true;
    if (send.splitNodeId != null && send.splitNodeId !== parent.splitNodeId) return true;
    return false;
};

/**
 * Walks up the parentIndex chain from `send` and returns the splitNodeId of the nearest
 * enclosing conditional split (the send itself first, then its ancestors). Returns null when
 * no ancestor carries a splitNodeId (top-level split or a non-nested branch).
 *
 * Used to resolve a nested split's sibling branch: a nested MEETS branch repeats its parent
 * branch's splitType, so climbing by effective splitType (getBranchRootParentIndex) overshoots
 * to the OUTER split. The splitNodeId pins the resolution to the correct nested split.
 */
export const findEnclosingSplitNodeId = (send: any, sends: CampaignSend[]): string | null => {
    let current: any = send;
    while (current != null) {
        if (current.splitNodeId != null) return current.splitNodeId;
        current = current.parentIndex != null ? sends[current.parentIndex] : null;
    }
    return null;
};

export const getBranchRootParentIndex = (send: any, sends: CampaignSend[]): number => {
    if (send?.splitType) {
        if (send?.parentIndex != null) {
            const parent = sends[send.parentIndex] as any;
            if (getEffectiveSplitType(parent, sends) === send.splitType) {
                return getBranchRootParentIndex(parent, sends);
            }
        }
        return send.parentIndex ?? 0;
    }
    if (send?.parentIndex != null) {
        return getBranchRootParentIndex(sends[send.parentIndex], sends);
    }
    return 0;
};

/**
 * When completing the "does not meet criteria" branch of a nested conditional split,
 * returns the index of the nearest ancestor split's "does not meet criteria" branch root.
 * Returns -1 when there is no ancestor split (top-level split → proceed to schedule).
 *
 * Algorithm:
 *  1. getBranchRootParentIndex walks up to the DNM root and returns the index of the send
 *     that is the parent of the inner split (may be a plain chained send, not the branch root).
 *  2a. If that send has a splitNodeId in its ancestor chain (nested split), use it to locate
 *     the sibling DNM.
 *  2b. Otherwise (top-level split, no splitNodeId): walk up via parentIndex from that send until
 *     we find the enclosing MEETS branch root (isSplitBranchRootSend + splitType=MEETS). Then
 *     look for a DOES_NOT_MEET sibling with the same parentIndex, excluding activeSend itself
 *     (the exclusion handles the case where activeSend IS the top-level DNM — finding itself
 *     would be wrong).
 */
export const findAncestorDoesNotMeetIndex = (activeSend: any, sends: CampaignSend[]): number => {
    const dnmRootParentIndex = getBranchRootParentIndex(activeSend, sends);
    const outerSend = sends[dnmRootParentIndex] as any;
    const outerSplitNodeId = findEnclosingSplitNodeId(outerSend, sends);
    if (outerSplitNodeId) {
        return sends.findIndex(
            (s: any) => s.splitNodeId === outerSplitNodeId && s.splitType === SplitTypes.DOES_NOT_MEET
        );
    }
    // Walk up from outerSend to find the nearest MEETS branch root (the outer split's MEETS side).
    // The DNM sibling of that branch root is the ancestor we need.
    let current: any = outerSend;
    while (current != null) {
        if (isSplitBranchRootSend(current, sends) && current.splitType === SplitTypes.MEETS) {
            const meetsBranchRoot = current;
            return sends.findIndex(
                (s: any) =>
                    s !== activeSend &&
                    s.splitType === SplitTypes.DOES_NOT_MEET &&
                    s.parentIndex === meetsBranchRoot.parentIndex &&
                    !s.splitNodeId
            );
        }
        current = current.parentIndex != null ? (sends[current.parentIndex] as any) : null;
    }
    return -1;
};

/**
 * Returns the indexes of sends that are missing something required before publish:
 *
 * - Content: any send that will actually SEND (not exit) needs real content for its channel.
 *   `ADD_SPLIT_NODE`/`addBranchSend` create branch sends with `splitAction: SEND` immediately,
 *   before the user has picked content, so `splitType`/`splitAction` truthiness alone can't be
 *   used to gate publish — this checks the content field itself (email: templateId, sms/direct
 *   mail/voice: content.id).
 * - Criteria: any split branch ROOT (`isSplitBranchRootSend`) needs its audience criteria
 *   defined — either the legacy flat `send.segments` (set via `setSegment`/`SET_SEGMENT`) or the
 *   nested `send.segmentDefinition` (set via `SET_SPLIT_SEGMENT_DEFINITION`) — regardless of
 *   whether that branch's own action ends up being SEND or EXIT — the split itself is
 *   meaningless without it.
 */
export const getIncompleteSendIndexes = (sends: CampaignSend[]): number[] =>
    sends.reduce<number[]>((incomplete, send, index) => {
        const anySend = send as any;
        const isExit = anySend.splitAction === SplitActions.EXIT || anySend.channel === CampaignChannels.NOOP;

        const missingContent =
            !isExit && (anySend.channel === CampaignChannels.EMAIL ? !anySend.templateId : !anySend.content?.id);
        const missingCriteria =
            isSplitBranchRootSend(anySend, sends) && !anySend.segmentDefinition && !anySend.segments?.length;

        if (missingContent || missingCriteria) incomplete.push(index);

        return incomplete;
    }, []);

export const isCampaignSendsComplete = (sends: CampaignSend[]): boolean => getIncompleteSendIndexes(sends).length === 0;

export const getDelayValue = (timeDifference: number): number =>
    timeDifference % 24 ? timeDifference : timeDifference / 24;

export const getDelayUnit = (timeDifference: number): string => (timeDifference % 24 ? 'hours' : 'days');

export const getTimeDifferenceInHours = (firstTime: string, secondTime?: string): number => {
    if (!secondTime) return;

    const firstDate = DateTime.fromISO(firstTime);
    const secondDate = DateTime.fromISO(secondTime);

    // Handle small rounding differences by calculating the time difference then rounding it.
    const timeDifference = parseFloat(Math.abs(secondDate.diff(firstDate, 'hours').as('hours')).toFixed(2));

    return timeDifference;
};

export const getDelayUnitAndValue = (parentAudienceDelayInterval?: string) => {
    if (!parentAudienceDelayInterval)
        return {
            initialDelayValue: '',
            initialDelayUnit: '',
        };
    const words = parentAudienceDelayInterval.split(' ');
    const initialDelayValue = words[0];
    const initialDelayUnit = words[1];
    return { initialDelayValue, initialDelayUnit };
};

export const getDelayedSendTime = (previousSendTimestamp: string, delayValue?: string, delayUnit?: string) => {
    if (!delayValue || !delayUnit) return;

    const formattedDelayValue = Number(delayValue);
    return DateTime.fromISO(previousSendTimestamp || '')
        .plus({ [delayUnit]: formattedDelayValue })
        .toISO();
};

export const getSegmentsForApi = (
    send: EmailSendInputState,
    sendForApi: EmailSendInput,
    sends: CampaignSend[] = []
) => {
    // Presently, we're only supporting a single segment (hence the 0).
    const firstSegment = send.segments[0];
    const { name, operator, category, categorySpecificFields } = firstSegment;
    const isDoesNotMeetCriteriaSend = send?.splitType === SplitTypes.DOES_NOT_MEET;

    if (category === FilterCategory.CONTACT_ACTIVITY) {
        // Handle both cases:
        // 1. NEW curation: "does not meet criteria" already has API operator from reducer
        // 2. EDITING draft: operator is in UI format (converted by getEngagementFilter)
        const operatorForApi = isApiOperator(operator as string)
            ? ((operator as unknown) as EngagementAPIOperator)
            : engagementUiToApiOperator[operator as EngagementUIOperator];

        // Resolve the selected send reference.
        // Same-curation sends (sentinel or saved draft ID) are encoded as a parentSendIndex so
        // the backend can map it to the freshly-created send ID inside the create/update
        // transaction. Sends from other curations have stable DB IDs and are passed as-is.
        const rawValue = categorySpecificFields?.value;
        let resolvedValue: number | null = null;
        let parentSendIndex: number | undefined;

        if (typeof rawValue === 'number') {
            if (rawValue < 0) {
                // Sentinel -(i+1) — unsaved same-curation send at position i
                parentSendIndex = -rawValue - 1;
            } else if (rawValue > 0) {
                const sendIdx = sends.findIndex((s) => (s as any).id === rawValue);
                if (sendIdx >= 0) {
                    // Saved draft send belonging to this curation — resolve by position
                    parentSendIndex = sendIdx;
                } else {
                    // Send from a different curation — stable ID, pass through directly
                    resolvedValue = rawValue;
                }
            }
        }

        sendForApi.segments = [
            {
                name: name as EngagementName,
                operator: operatorForApi as EngagementAPIOperator,
                value: resolvedValue,
                ...(parentSendIndex !== undefined && { parentSendIndex }),
                ...(categorySpecificFields?.sendInteractionField?.value && {
                    segmentLinkValue: categorySpecificFields.sendInteractionField.value as string,
                }),
            },
        ];
    }

    if (category === FilterCategory.CONTACT_DETAILS) {
        sendForApi.segments = [];
        sendForApi.segmentDefinition = {
            matchType: 'AND',
            contactEvent: [],
            contactObject: [],
            demographic: [
                {
                    name,
                    operator: getInclusionOperatorFromUIToAPI(operator) as SegmentDemographic['operator'],
                    value: categorySpecificFields?.value,
                },
            ],
            engagement: [],
            ...(isDoesNotMeetCriteriaSend && { negateQuery: true }),
        };
    }

    if (category === FilterCategory.CONTACT_EVENTS) {
        sendForApi.segments = [];
        sendForApi.segmentDefinition = {
            matchType: 'AND',
            contactEvent: [convertFilterToEvent(firstSegment)],
            contactObject: [],
            demographic: [],
            engagement: [],
            ...(isDoesNotMeetCriteriaSend && { negateQuery: true }),
        };
    }

    if (category === FilterCategory.CONTACT_OBJECTS) {
        sendForApi.segments = [];
        sendForApi.segmentDefinition = {
            matchType: 'AND',
            contactEvent: [],
            contactObject: [convertFilterToEvent(firstSegment)],
            demographic: [],
            engagement: [],
            ...(isDoesNotMeetCriteriaSend && { negateQuery: true }),
        };
    }
};

/**
 * Builds the API payload for a single email send, for both "Save as Draft" and Publish.
 */
export const getEmailSendForApi = (
    send: EmailSendInputState,
    index: number,
    campaignState: CampaignStateTypes
): EmailSendInput | ExitActionInput => {
    if (send?.splitAction === SplitActions.EXIT) {
        const { parentIndex, splitAction, splitType, parentAudienceDelayInterval, segmentId } = send;

        const exitActionInput: ExitActionInput = {
            channel: CampaignChannels.NOOP,
            parentIndex,
            splitAction,
            splitType,
            ...(parentAudienceDelayInterval && { parentAudienceDelayInterval }),
            ...(segmentId && { segmentId }),
            ...(send.splitNodeId != null && { splitNodeId: send.splitNodeId }),
        };

        if (send.segmentDefinition) {
            exitActionInput.segmentDefinition = send.segmentDefinition;
        } else if (send.segments && send.segments.length > 0) {
            getSegmentsForApi(send, exitActionInput as any, campaignState.sends);
        }

        return exitActionInput;
    }

    let sendForApi: EmailSendInput = {
        channel: send.channel || CampaignChannels.EMAIL,
        name: send.name,
        preHeader: send.preHeader,
        senderProfileId: send.senderProfileId,
        subjectLine: send.subjectLine,
        templateId: send.templateId,
        segmentId: send.segmentId || null, // Always include segmentId, even if null
    };

    // A/B Testing: Both Email-A (index 0) and Email-B (index 1) should have scheduledTimestamp
    const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;
    const isABTesting = isABTestingContent || isABTestingSendTime;
    const shouldIncludeSchedule = index === 0 || (isABTesting && index === 1);

    if (shouldIncludeSchedule) {
        // For A/B testing, Email-B should inherit segmentId and hasRecommendedTime from Email-A
        const isEmailB = isABTesting && index === 1;
        const emailASend = isEmailB ? (campaignState.sends[0] as EmailSendInputState) : null;

        // Determine which values to use for segmentId and hasRecommendedTime
        const segmentIdToUse = isEmailB ? emailASend?.segmentId : send.segmentId;
        const hasRecommendedTimeToUse = isEmailB ? emailASend?.hasRecommendedTime : send.hasRecommendedTime;
        const recurrenceDayOfWeekToUse = isEmailB ? emailASend?.recurrenceDayOfWeek : send.recurrenceDayOfWeek;
        const recurrenceOccurrencePositionToUse = isEmailB
            ? emailASend?.recurrenceOccurrencePosition
            : send.recurrenceOccurrencePosition;

        return {
            ...sendForApi,
            recurrenceFrequency: send.recurrenceFrequency,
            scheduledTimestamp: send.scheduledTimestamp,
            segmentId: segmentIdToUse || null, // Always include segmentId, even if null
            ...(send.cohortId && { cohortId: send.cohortId }),
            ...(send.scheduledEndTimestamp && { scheduledEndTimestamp: send.scheduledEndTimestamp }),
            ...(hasRecommendedTimeToUse !== undefined && { hasRecommendedTime: hasRecommendedTimeToUse }),
            ...(recurrenceDayOfWeekToUse != null && { recurrenceDayOfWeek: recurrenceDayOfWeekToUse }),
            ...(recurrenceOccurrencePositionToUse != null && {
                recurrenceOccurrencePosition: recurrenceOccurrencePositionToUse,
            }),
        };
    }

    if (send.segmentDefinition) {
        sendForApi.segmentDefinition = send.segmentDefinition;
    } else if (!!send.segments) {
        getSegmentsForApi(send, sendForApi, campaignState.sends);
    }

    // Include splitType for the root send of a split branch — not for chained sends that
    // merely inherit the branch's splitType (those are re-derived on load by walking the
    // parentIndex chain up to their branch root). A nested split's branch root legitimately
    // repeats its parent branch's splitType, so this must key off isSplitBranchRootSend
    // (which understands splitNodeId) rather than a naive parentSplitType !== splitType
    // check, which would strip the nested branch's type.
    //
    // splitAction must NOT gate this: a branch root that hasn't had its path action chosen
    // yet (splitAction still null, e.g. the outer split's DOES_NOT_MEET branch before it's
    // ever visited) still needs splitType persisted, or the branch loses its identity on the
    // next save/reload — it comes back as an unpaired, unlabeled plain send.
    if (!!send.splitType && isSplitBranchRootSend(send, campaignState.sends)) {
        sendForApi = {
            ...sendForApi,
            splitType: send.splitType,
            ...(send.splitAction && { splitAction: send.splitAction }),
        };
    }

    return {
        ...sendForApi,
        parentIndex: send.parentIndex,
        parentAudienceDelayInterval: send.parentAudienceDelayInterval,
        // Frontend-only editing aid persisted via draft_structure so nested splits can be
        // re-detected on reload. The backend stores it verbatim in JSONB and ignores it
        // for the normalized send records.
        ...(send.splitNodeId != null && { splitNodeId: send.splitNodeId }),
    };
};
